import { useCallback } from "react";

import firebase from "firebase/app";

import { useBlockingProgress } from "../components/blocking-progress";
import { useSnackbar } from "../components/snackbar";
import { Task } from "../registration/registration.model";
import { Cooperation, Guide, GuideMode } from "../gardener/gardener.model";
import { SolvedTaskItem } from "../cooperation/blocking-progress/SolvedTaskItem";
import { FailedTaskItem } from "../cooperation/blocking-progress/FailedTaskItem";
import { LevelUpItem } from "../cooperation/blocking-progress/LevelUpItem";
import { LevelDownItem } from "../cooperation/blocking-progress/LevelDownItem";

export interface BackendMessage {
    type: "backend message",
    severity: "error" | "warning" | "success" | "info";
    autoHideDuration: number | null;
    message: string;
}

export interface LimitReachedFlashMessage {
    type: "flash message";
    message: "limit reached";
    cooperationLevel: Exclude<Cooperation["level"], "platin">;
    tasksUntilNextLevel: number;
}

export interface SolvedTaskFlashMessage {
    type: "flash message";
    message: "solved task";
    cooperationLevel: Exclude<Cooperation["level"], "platin">;
    tasksUntilNextLevel: number;
}

export interface FailedTaskFlashMessage {
    type: "flash message";
    message: "failed task";
    cooperationLevel: Exclude<Cooperation["level"], "platin">;
    tasksUntilNextLevel: number;
}

export type LevelUpFlashMessage = {
    type: "flash message";
    message: "level up"
    cooperationLevel: Exclude<Cooperation["level"], "bronze" | "platin">;
    tasksUntilNextLevel: number;
} | {
    type: "flash message";
    message: "level up";
    cooperationLevel: "platin";
    tasksUntilNextLevel: null;
}

export interface LevelDownFlashMessage {
    type: "flash message";
    message: "level down";
    cooperationLevel: Exclude<Cooperation["level"], "platin">;
    tasksUntilNextLevel: number;
}

export type FlashMessage = LimitReachedFlashMessage | SolvedTaskFlashMessage | FailedTaskFlashMessage | LevelUpFlashMessage | LevelDownFlashMessage;

export type TaskResponseData =
    | CreateAppointmentData
    | CreateAdditionalAppointmentData
    | UpdateAppointmentData
    | CancelAppointmentData
    | RejectTaskBecauseGardenerCouldNotFindDateWithCustomerData
    | RejectTaskBecauseGardenerSaysCustomerDoesNotNeedHelpData
    | RejectTaskBecauseGardenerSaysCustomerMightNeedHelpData
    | RejectTaskBecauseGardenerCouldNotHelpCustomerData
    | RegisterContactAttemptData
    | PostponeContactData
    | CreateRegistrationData
    | AskForHelpData
    | MarkGuideCompletedData
    | UpdateIndividualSellerData | UpdateEntitySellerData
    | UpdateContactInformationData
    | StopWorkingForCustomerData

export interface CreateAppointmentData {
    action: "create appointment";
    taskId: string;
    date: string;
    description: string;
}

export interface CreateAdditionalAppointmentData {
    action: "create additional appointment";
    customerId: string;
    date: string;
    description: string;
}

export interface UpdateAppointmentData {
    action: "update appointment";
    taskId: string;
    hasVisited: boolean;
    date: string;
    description: string;
}

export interface CancelAppointmentData {
    action: "cancel appointment";
    taskId: string;
    hasVisited: boolean;
}

export interface RejectTaskBecauseGardenerCouldNotFindDateWithCustomerData {
    action: "reject task because gardener could not find date with customer",
    taskId: string;
    reason: string,
}

export interface RejectTaskBecauseGardenerSaysCustomerDoesNotNeedHelpData {
    action: "reject task because gardener says customer does not need help",
    taskId: string;
    reason: string,
}

export interface RejectTaskBecauseGardenerSaysCustomerMightNeedHelpData {
    action: "reject task because gardener says customer might need help",
    taskId: string;
    reason: string,
}

export interface RejectTaskBecauseGardenerCouldNotHelpCustomerData {
    action: "reject task because gardener could not help customer",
    taskId: string;
    reason: string,
}

export interface RegisterContactAttemptData {
    action: "register contact attempt",
    taskId: string;
    shouldGiveUp: boolean;
}

export interface PostponeContactData {
    action: "postpone contact",
    taskId: string;
    reason: string,
    date: string,
}

export interface CreateRegistrationData {
    action: "create registration",
    taskId: string;
    tasks: Task[],
}

export interface AskForHelpData {
    action: "ask for help";
    message: string;
}

export interface MarkGuideCompletedData {
    action: "mark guide completed";
    mode: GuideMode,
    guide: Guide;
}

export interface UpdateIndividualSellerData {
    action: "update individual seller";
    name: string;
    nameChangeReason: string | null;
    externalAddressId: string;
    cpr: string;
    cprChangeReason: string | null;
    cvr: string | null;
}

export interface UpdateEntitySellerData {
    action: "update entity seller";
    externalAddressId: string;
}

export interface UpdateContactInformationData {
    action: "update contact information";
    email: string;
    phoneNumber: string;
}

export interface StopWorkingForCustomerData {
    action: "stop working for customer",
    customerId: string,
}

const taskResponse: (data: TaskResponseData) => Promise<firebase.functions.HttpsCallableResult>
    = firebase.app().functions("europe-west3").httpsCallable("Match-taskResponse2");

export const useTaskResponseAction = (callback?: (action: TaskResponseData["action"]) => void) => {
    const { block, flashMessage, unblock } = useBlockingProgress();
    const { openSnackbar } = useSnackbar();

    return useCallback((data: TaskResponseData | string) => {
        if ( typeof data === "string" ) return;

        block();

        taskResponse(data)
            .then((response) => {
                if ( response.data && response.data.type === "backend message" ) {
                    const data = response.data as BackendMessage;
                    unblock();
                    openSnackbar(data.severity, data.message, data.autoHideDuration);
                    return;
                }

                if ( response.data && response.data.type === "flash message" ) {
                    const onFlashMessageSuccess = () => {
                        openSnackbar("success", "Besvarelsen er modtaget");
                        unblock();
                        if ( callback ) callback(data.action);
                    };

                    const responseData = response.data as FlashMessage;

                    if ( responseData.message === "solved task" ) {
                        return flashMessage(<SolvedTaskItem cooperationLevel={responseData.cooperationLevel} tasksUntilNextLevel={responseData.tasksUntilNextLevel} />).then(onFlashMessageSuccess);
                    }

                    if ( responseData.message === "failed task" ) {
                        return flashMessage(<FailedTaskItem cooperationLevel={responseData.cooperationLevel} tasksUntilNextLevel={responseData.tasksUntilNextLevel} />).then(onFlashMessageSuccess);
                    }

                    if ( responseData.message === "level up" ) {
                        const item = responseData.cooperationLevel !== "platin" ? (
                            <LevelUpItem cooperationLevel={responseData.cooperationLevel} tasksUntilNextLevel={responseData.tasksUntilNextLevel} />
                        ) : (
                            <LevelUpItem cooperationLevel={responseData.cooperationLevel} tasksUntilNextLevel={responseData.tasksUntilNextLevel} />
                        );
                        return flashMessage(item).then(onFlashMessageSuccess);
                    }

                    if ( responseData.message === "level down" ) {
                        return flashMessage(<LevelDownItem cooperationLevel={responseData.cooperationLevel} tasksUntilNextLevel={responseData.tasksUntilNextLevel} />).then(onFlashMessageSuccess);
                    }
                }

                if ( response.data === "has-already-scheduled-appointment" ) {
                    openSnackbar("warning", "Du har allerede aftalt et besøg med kunden på denne dato");
                    unblock();
                    return;
                }

                openSnackbar("success", "Besvarelsen er modtaget");
                unblock();
                if ( callback ) callback(data.action);
            })
            .catch(() => {
                openSnackbar("error", "Der skete en fejl. Besvarelsen er ikke blevet modtaget.");
                unblock();
            });

    }, [block, flashMessage, unblock, openSnackbar, callback]);
};
